import { useState, useEffect } from 'react';
import { Grid, Menu, MenuItem, ListItemText, ListItemIcon } from '@mui/material';
import { Typography, Checkbox, Button } from '@mui/material';
import { CheckBoxOutlineBlankRounded, CheckBoxRounded, ArrowDropDownRounded } from '@mui/icons-material';
import { NotInterested, VisibilityRounded } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { isUuid } from '../../Functions';
import { isArray, isEmpty } from 'lodash';
import SelectedOptionsDialog from '../SelectedOptionsDialog';

export default function TickboxCell({
  column,
  initial,
  onChange,
  editable,
  textAlign,
  disableClick,
  disabled,
  columnWidth,
}) {
  const [values, setValues] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const value = initial?.value;
    setValues(isArray(value) && value.every(isUuid) ? value : []);
  }, [initial]);

  if (!column) return null;

  const options = column.options;
  const withImages = column.displayType === 'images';

  // === no options set ===
  if (!isArray(options) || isEmpty(options)) {
    return <Typography color="textSecondary">Set options</Typography>;
  }

  const selectedOptions = options.filter((x) => values.includes(x.id));
  const selectedOptionsTexts = selectedOptions.map((x) => x.text);
  const selectedOptionsImages = withImages ? selectedOptions.filter((x) => x.image).map((x) => x.image) : [];

  if (editable) {
    return (
      <Grid container justifyContent={textAlign}>
        <Button
          style={{ textTransform: 'none', justifyContent: textAlign || 'flex-start' }}
          onClick={disableClick ? undefined : (e) => setMenuAnchor(e.currentTarget)}
          startIcon={<ArrowDropDownRounded style={{ color: grey[500] }} />}
          fullWidth
          disabled={Boolean(disabled)}
        >
          {selectedOptionsImages.map((src) => (
            <Grid item component={'img'} src={src} alt="img" sx={{ maxWidth: 30, maxHeight: 24, mr: 0.5 }} />
          ))}
          <Typography noWrap sx={{ fontWeight: 500, color: (theme) => theme.palette.text.secondary }}>
            {!isEmpty(selectedOptionsTexts)
              ? selectedOptionsTexts.join(', ')
              : `Pick option${column.selectType === 'multiple' ? 's' : ''}`}
          </Typography>
        </Button>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          style={{ minWidth: columnWidth }}
          slotProps={{ paper: { style: { minWidth: columnWidth } } }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.id}
              onClick={() => {
                if (values.includes(option.id)) {
                  if (column.selectType === 'multiple') {
                    const newValue = values.filter((x) => x !== option.id).sort();
                    setValues(newValue);
                    onChange({ value: newValue });
                  } else {
                    setValues([]);
                    onChange({ value: [] });
                  }
                } else {
                  if (column.selectType === 'multiple') {
                    const newValue = [...values, option.id].sort();
                    setValues(newValue);
                    onChange({ value: newValue });
                  } else {
                    setValues([option.id]);
                    onChange({ value: [option.id] });
                  }
                }
              }}
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankRounded fontSize="small" />}
                checkedIcon={<CheckBoxRounded fontSize="small" />}
                color="primary"
                checked={values.includes(option.id)}
              />

              {withImages && option.image && (
                <Grid
                  item
                  component={'img'}
                  src={option.image}
                  alt={option.text}
                  sx={{ maxWidth: 50, maxHeight: 50, mx: 0.5 }}
                />
              )}

              <ListItemText primary={option.text} />
            </MenuItem>
          ))}
          {!isEmpty(values) && (
            <MenuItem
              onClick={() => {
                setValues([]);
                onChange({ value: [] }, true);
                setMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <NotInterested color="error" />
              </ListItemIcon>
              Clear
            </MenuItem>
          )}
        </Menu>
      </Grid>
    );
  }

  if (isEmpty(values)) return null;

  // if only one option selected - show just it in the cell
  if (selectedOptionsTexts.length === 1)
    return (
      <Grid container justifyContent={textAlign} alignItems={'center'}>
        {selectedOptionsImages.map((src) => (
          <Grid item component={'img'} src={src} alt="img" sx={{ maxWidth: 30, maxHeight: 24, mr: 0.5 }} />
        ))}
        <Typography noWrap style={{ maxWidth: columnWidth, fontWeight: 500 }}>
          {selectedOptionsTexts[0]}
        </Typography>
      </Grid>
    );

  // multiple options selected - show dialod
  return (
    <Grid container justifyContent={textAlign}>
      <Button
        style={{ textTransform: 'none', justifyContent: textAlign || 'flex-start' }}
        onClick={() => setDialogOpen(true)}
        startIcon={<VisibilityRounded style={{ color: grey[500] }} />}
        fullWidth
        disabled={Boolean(disabled)}
      >
        {selectedOptionsImages.map((src) => (
          <Grid item component={'img'} src={src} alt="img" sx={{ maxWidth: 30, maxHeight: 24, mr: 0.5 }} />
        ))}
        <Typography noWrap style={{ maxWidth: '100%' }}>
          {selectedOptionsTexts.join(', ')}
        </Typography>
      </Button>

      <SelectedOptionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        options={selectedOptions}
        withImages={withImages}
      />
    </Grid>
  );
}
